import { Component, lazy, Suspense, type ComponentType, type CSSProperties, type LazyExoticComponent, type ReactElement, type ReactNode } from "react";

import type { ModuleOverlayElementProps } from "../modules/contract";
import { MODULE_OVERLAY_ELEMENTS } from "../modules/overlay-element-registry";
import { VariableValueView } from "./variable-view";
import type { BoundOverlayData, OverlayElementData, OverlayLanguage } from "./model";

interface OverlayCanvasProperties {
  overlay: BoundOverlayData;
  language: OverlayLanguage;
  variables: Readonly<Record<string, number>>;
  elementId: string | null;
}

interface ElementBoundaryProperties {
  children: ReactNode;
}

interface ElementBoundaryState {
  failed: boolean;
}

class ElementErrorBoundary extends Component<ElementBoundaryProperties, ElementBoundaryState> {
  public override state: ElementBoundaryState = { failed: false };

  public static getDerivedStateFromError(): ElementBoundaryState {
    return { failed: true };
  }

  public override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

const lazyModuleElements = new Map<string, LazyExoticComponent<ComponentType<ModuleOverlayElementProps>>>(
  MODULE_OVERLAY_ELEMENTS.map(({ definition }) => [definition.kind, lazy(definition.load)]),
);

const renderElement = (
  element: OverlayElementData,
  language: OverlayLanguage,
  variables: Readonly<Record<string, number>>,
  isolated: boolean,
): ReactElement | null => {
  const scale = element.scalePercent / 100;
  // Width must stay intrinsic and independent of `left`/`x` in the composition: an absolutely
  // positioned box otherwise shrinks to the remaining space near the canvas edge, so the measured
  // size (and thus the editor's position clamp) would depend on where the element sits. The
  // isolated/legacy path (a single element at its standalone origin, e.g. an unbound #text= link
  // rendered into a narrow OBS browser source) keeps its original shrink-to-fit/wrap behavior.
  const style: CSSProperties = {
    position: isolated ? "relative" : "absolute",
    left: isolated ? 0 : element.x,
    top: isolated ? 0 : element.y,
    ...(isolated ? {} : { width: "max-content" }),
    transform: isolated ? `scale(${String(scale)})` : `scale(${String(scale)}) translateX(var(--brobot-overlay-anchor-x, 0%))`,
    transformOrigin: "top left",
    zIndex: element.z,
  };
  const wrapperClass = isolated ? undefined : "brobot-overlay-composition-element";
  if (element.kind !== "variable") {
    const ModuleElement = lazyModuleElements.get(element.kind);
    if (ModuleElement === undefined || element.moduleEnabled !== true) return null;
    return <div
      data-element={element.id}
      data-kind={element.kind}
      className={wrapperClass}
      style={style}
    >
      <Suspense fallback={null}><ModuleElement
        elementId={element.id}
        config={element.config ?? {}}
        state={element.state ?? null}
        now={Date.now()}
        language={language}
      /></Suspense>
    </div>;
  }
  if (element.variableName === null || !Object.hasOwn(variables, element.variableName)) return null;
  const value = variables[element.variableName];
  if (value === undefined) return null;
  return <div
    data-element={element.id}
    data-kind={element.kind}
    className={wrapperClass}
    style={style}
  >
    <VariableValueView
      name={element.variableName}
      text={element.text}
      value={value}
      language={language}
      variables={variables}
    />
  </div>;
};

/** Draws the stored composition, or the same element at its standalone origin. */
export const OverlayCanvas = ({ overlay, language, variables, elementId }: OverlayCanvasProperties): ReactElement | null => {
  const isolatedElement = elementId === null
    ? null
    : overlay.elements.find((element) => element.id === elementId) ?? null;
  if (elementId !== null && isolatedElement === null) return null;

  const elements = isolatedElement === null
    ? [...overlay.elements].filter((element) => element.inComposition).sort((left, right) => left.z - right.z)
    : [isolatedElement];
  const canvasStyle: CSSProperties = isolatedElement === null
    ? { position: "relative", width: overlay.width, height: overlay.height }
    : { position: "relative", width: "max-content", height: "max-content" };

  return <div className="brobot-overlay" style={canvasStyle}>
    {elements.map((element) => {
      const value = element.variableName !== null && Object.hasOwn(variables, element.variableName)
        ? variables[element.variableName]
        : null;
      const resetKey = JSON.stringify([
        overlay.revision,
        element.id,
        element.kind,
        element.label,
        element.variableName,
        element.text,
        element.config,
        element.moduleEnabled,
        language,
        value,
        isolatedElement !== null,
      ]);
      return <ElementErrorBoundary key={resetKey}>
      {renderElement(element, language, variables, isolatedElement !== null)}
      </ElementErrorBoundary>;
    })}
  </div>;
};
